import type {
  Certificate,
  IdentifyInput,
  Query,
  Recording,
  Shark,
  SharkPage,
  SharkSort,
  Stats,
} from "@/api/types";

/** An HTTP error from the API, carrying the backend's `{ detail }` message. */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
  }

  /** 5xx and 429 are worth retrying; other 4xx are the caller's problem. */
  get transient(): boolean {
    return this.status >= 500 || this.status === 429;
  }
}

async function readDetail(response: Response): Promise<string> {
  const fallback = `${response.status} ${response.statusText || "error"}`.trim();
  const text = await response.text();
  if (!text) return fallback;
  try {
    const body: unknown = JSON.parse(text);
    if (body && typeof body === "object" && "detail" in body) {
      const { detail } = body as { detail: unknown };
      return typeof detail === "string" ? detail : JSON.stringify(detail);
    }
  } catch {
    // A non-JSON error page (proxy, crash); show its first line rather than nothing.
    return `${fallback}: ${text.slice(0, 120)}`;
  }
  return fallback;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { Accept: "application/json", ...init?.headers },
  });
  if (!response.ok) throw new ApiError(response.status, await readDetail(response));
  return (await response.json()) as T;
}

function postJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 200 * 1024 * 1024;

function uploadForm(input: IdentifyInput): FormData {
  const form = new FormData();
  form.append("file", input.file);
  if (input.observer) form.append("observer", input.observer);
  if (input.date) form.append("date", input.date);
  if (input.locality) form.append("locality", input.locality);
  if (input.lat !== undefined) form.append("lat", String(input.lat));
  if (input.lon !== undefined) form.append("lon", String(input.lon));
  return form;
}

export function identify(input: IdentifyInput): Promise<Query> {
  return request<Query>("/api/identify", { method: "POST", body: uploadForm(input) });
}

/** Upload a video; the server samples it at a low frame rate and identifies every frame. */
export function createRecording(input: IdentifyInput): Promise<Recording> {
  return request<Recording>("/api/recordings", { method: "POST", body: uploadForm(input) });
}

export function getQuery(id: string, signal?: AbortSignal): Promise<Query> {
  return request<Query>(`/api/queries/${encodeURIComponent(id)}`, { signal });
}

export function getRecording(id: string, signal?: AbortSignal): Promise<Recording> {
  return request<Recording>(`/api/recordings/${encodeURIComponent(id)}`, { signal });
}

export function confirmQuery(id: string, sharkId: string, observer: string): Promise<Certificate> {
  return postJson(`/api/queries/${encodeURIComponent(id)}/confirm`, {
    shark_id: sharkId,
    observer,
  });
}

export function nameQuery(id: string, name: string, observer: string): Promise<Certificate> {
  return postJson(`/api/queries/${encodeURIComponent(id)}/name`, { name, observer });
}

export interface SharkListParams {
  sort?: SharkSort;
  q?: string;
  offset?: number;
  limit?: number;
}

export function listSharks(params: SharkListParams, signal?: AbortSignal): Promise<SharkPage> {
  const search = new URLSearchParams();
  if (params.sort) search.set("sort", params.sort);
  if (params.q) search.set("q", params.q);
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  const qs = search.toString();
  return request<SharkPage>(`/api/sharks${qs ? `?${qs}` : ""}`, { signal });
}

export function getShark(id: string, signal?: AbortSignal): Promise<Shark> {
  return request<Shark>(`/api/sharks/${encodeURIComponent(id)}`, { signal });
}

export function getCertificate(id: string, signal?: AbortSignal): Promise<Certificate> {
  return request<Certificate>(`/api/certificates/${encodeURIComponent(id)}`, { signal });
}

export function getStats(signal?: AbortSignal): Promise<Stats> {
  return request<Stats>("/api/stats", { signal });
}

// ------------------------------------------------------------------ polling

export const POLL_INTERVAL_MS = 1500;
export const MAX_CONSECUTIVE_POLL_ERRORS = 4;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function isRetryable(error: unknown): boolean {
  if (error instanceof ApiError) return error.transient;
  // fetch rejects with TypeError on network failure; aborts must propagate.
  return error instanceof TypeError;
}

export interface PollOptions<T> {
  signal?: AbortSignal;
  intervalMs?: number;
  maxConsecutiveErrors?: number;
  /** Called with every fetched snapshot, including the final one. */
  onUpdate?: (snapshot: T) => void;
}

/** Something the server works on in the background, polled until it leaves `processing`. */
type Pollable = { status: string };

/**
 * Poll `fetchOnce` until the status leaves `processing`.
 *
 * Transient failures (network, 5xx, 429) are retried on the same cadence; after
 * `maxConsecutiveErrors` in a row the last error is thrown. Non-transient errors (404 etc.)
 * and aborts are thrown immediately.
 */
async function poll<T extends Pollable>(
  fetchOnce: (signal?: AbortSignal) => Promise<T>,
  options: PollOptions<T>,
): Promise<T> {
  const {
    signal,
    intervalMs = POLL_INTERVAL_MS,
    maxConsecutiveErrors = MAX_CONSECUTIVE_POLL_ERRORS,
    onUpdate,
  } = options;
  let failures = 0;
  for (;;) {
    try {
      // Sequential by design: one request in flight, then wait, then ask again.
      // oxlint-disable-next-line no-await-in-loop
      const snapshot = await fetchOnce(signal);
      failures = 0;
      onUpdate?.(snapshot);
      if (snapshot.status !== "processing") return snapshot;
    } catch (error) {
      if (signal?.aborted || !isRetryable(error)) throw error;
      failures += 1;
      if (failures >= maxConsecutiveErrors) throw error;
    }
    // oxlint-disable-next-line no-await-in-loop
    await sleep(intervalMs, signal);
  }
}

/** Poll `GET /api/queries/{id}` until the photo's outcome is in. */
export function pollQuery(id: string, options: PollOptions<Query> = {}): Promise<Query> {
  return poll((signal) => getQuery(id, signal), options);
}

/** Poll `GET /api/recordings/{id}` until every sampled frame has its outcome. */
export function pollRecording(
  id: string,
  options: PollOptions<Recording> = {},
): Promise<Recording> {
  return poll((signal) => getRecording(id, signal), options);
}
